
import React, { memo, useCallback, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import ShareControlModal from "./ShareControlModal";
import CommonButton from "../../Components/CommonButton";
import StatusBadge from "../../Components/StatusBadge";
import { lightTheme as T } from "../../Constants/Colors";
import { fontFamily, fontSize, fontWeight, spacing, subHeading2, body, bodySmall, label as labelFont } from "../../Constants/Fonts";


function getFileNameFromUrl(url, removeExtension = false) {
    if (!url) return "";

    const fullName = url.split("/").pop(); // filename with extension

    if (!removeExtension) {
        return fullName; // return as it is
    }

    // Remove extension
    const parts = fullName.split(".");
    parts.pop(); // remove last extension
    return parts.join(".");
}

// Small "label + value" meta row used for Domain / Control.
function MetaItem({ label, value }) {
    if (!value) return null;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
            <span style={labelFont({ color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.4px" })}>
                {label}
            </span>
            <StatusBadge variant="neutral" appearance="soft" dot={false} transform="uppercase">
                {value}
            </StatusBadge>
        </div>
    );
}

function QuestionCard({
    question,
    questionId,
    answered_by,
    shared_with_emails,
    index,
    domain,
    domainName,
    response,
    onChange,
    onFileUpload,
    onRemoveFile,
    onCheckWithNova,
    checkingId,
    submitQuestionAnswerHandler,
    resMsg,
    error,
    messageQuestionId,
    controlTitle,
    loading,
    reviewer_adjustment,
    reviewer_email,
    reviewer_remark,
    submissionStatus,
    hideShareButton = false,
    onRefreshQuestions,
}) {
    const qid = question?.control_question_id;
    const [shareModal, setShareModal] = useState(false);
    const resp = useMemo(() => {
        //Radio Data
        const fallbackAnswer =
            question?.answer_response ||
            "";
        //Textarea data
        const fallbackComments =
            question?.user_answer ||
            "";
        return {
            answer: response?.answer ?? fallbackAnswer ?? "",
            comments: response?.comments ?? fallbackComments ?? "",
            files: response?.files ?? [],
            submitted:
                response?.submitted ??
                !!(
                    (fallbackAnswer && fallbackAnswer.trim() !== "") ||
                    (fallbackComments && fallbackComments.trim() !== "")
                )
        };
    }, [question, response]);

    const isSaving = loading && messageQuestionId === qid;
    const isEditDisabled = submissionStatus && submissionStatus !== "draft";

    // Check if answer was already submitted before (from API response)
    const hasExistingAnswer =
        question?.answer_response ||
        question?.user_response ||
        question?.user_answer ||
        question?.answer ||
        (question?.question_answer && (question.question_answer.answer_response || question.question_answer.user_response || question.question_answer.user_answer || question.question_answer.answer)) ||
        (question?.answer_details && (question.answer_details.answer_response || question.answer_details.user_response || question.answer_details.user_answer || question.answer_details.answer)) ||
        resp?.submitted;

    const disableSave = isSaving || isEditDisabled;
    const buttonLabel = (() => {
        if (isSaving) return "Saving...";
        if (messageQuestionId === qid && resMsg) return hasExistingAnswer ? "Updated" : "Saved";
        if (hasExistingAnswer) return "Update";
        return "Save";
    })();

    const priority = question?.priority || question?.question?.priority;
    const severity = question?.severity || question?.question?.severity;
    const questionText = question?.question_text || question?.question?.question_text;
    const evidenceRequired = question?.evidence_required?.join(", ") || question?.question?.evidence_required?.join(", ");
    const evidenceSubmittedUrl = question?.evidence_submitted?.[0]?.url;

    //  FIX 4: Optimize radio button change handler
    const handleRadioChange = useCallback((e) => {
        const value = e.target.value;
        onChange(qid, "answer", value);
    }, [qid, onChange]);

    //  FIX 5: Optimize textarea change handler
    const handleTextareaChange = useCallback((e) => {
        const value = e.target.value;
        onChange(qid, "comments", value);
    }, [qid, onChange]);

    //  FIX 6: Optimize file upload handler
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileUpload(qid, file, domain?.organization?.id || domain?.id);
        }
        // Reset file input
        e.target.value = '';
    }, [qid, domain?.organization?.id, onFileUpload]);

    //  FIX 7: Optimize Foresee check handler
    const handleCheckNova = useCallback(() => {
        onCheckWithNova(qid);
    }, [qid, onCheckWithNova]);

    //  FIX 8: Optimize submit handler
    const handleSubmit = useCallback(() => {
        submitQuestionAnswerHandler(qid);
    }, [qid, submitQuestionAnswerHandler]);

    //  FIX 9: Optimize file remove handler
    const handleRemoveFile = useCallback((idx) => {
        onRemoveFile(qid, idx);
    }, [qid, onRemoveFile]);

    const closeShareModal = () => {
        setShareModal(false)
    }

    return (
        <div
            className="questionnaire_card"
            style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusLg,
                boxShadow: T.shadowSm,
                padding: spacing['3xl'],
                marginBottom: spacing['2xl'],
                fontFamily,
            }}
        >
            {/* Domain / Control meta */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: spacing['3xl'], marginBottom: spacing['2xl'] }}>
                <MetaItem label="Domain" value={domainName} />
                <MetaItem label="Control" value={controlTitle} />
            </div>

            {/* Question + priority / share */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: spacing['2xl'], marginBottom: spacing['2xl'] }}>
                <h5 style={subHeading2({ color: T.textPrimary, margin: 0, lineHeight: 1.45 })}>
                    {index + 1}. {questionText}
                </h5>
                <div style={{ display: "flex", gap: spacing.lg, flexShrink: 0, alignItems: "center" }}>
                    {priority && (
                        <StatusBadge variant="info" appearance="soft" dot={false} transform="none">
                            {priority}
                        </StatusBadge>
                    )}
                    {!hideShareButton && (
                        <CommonButton
                            text="Share"
                            variant="secondary"
                            onClick={() => setShareModal(true)}
                            disable={isEditDisabled}
                        />
                    )}
                </div>
            </div>

            {/* Evidence required */}
            {evidenceRequired && (
                <p style={body({ color: T.textSecondary, marginBottom: spacing.xl, lineHeight: 1.5 })}>
                    <span style={{ fontWeight: fontWeight.semibold, color: T.info }}>Evidence Required: </span>
                    {evidenceRequired}
                </p>
            )}

            {/* Evidence submitted */}
            {evidenceSubmittedUrl && (
                <p style={body({ color: T.textSecondary, marginBottom: spacing.xl })}>
                    <span style={{ fontWeight: fontWeight.semibold, color: T.textPrimary }}>Evidence Submitted: </span>
                    <span style={{ color: T.primary }}>{getFileNameFromUrl(evidenceSubmittedUrl)}</span>
                </p>
            )}

            {/* Severity */}
            {severity && (
                <div style={{ marginBottom: spacing['3xl'] }}>
                    <StatusBadge status={severity} dot={false} transform="none">
                        Severity: {String(severity).charAt(0).toUpperCase() + String(severity).slice(1)}
                    </StatusBadge>
                </div>
            )}

            {/* Answer radios */}
            <div style={{ display: "flex", gap: spacing['4xl'], marginBottom: spacing['2xl'], ...body({ color: T.textPrimary }) }}>
                {["Yes", "No", "Partial"].map((opt) => (
                    <Form.Check
                        key={opt}
                        inline
                        type="radio"
                        label={opt}
                        id={`radio-${qid}-${opt}`}
                        name={`question-${qid}`}
                        value={opt}
                        checked={resp.answer === opt}
                        onChange={handleRadioChange}
                        disabled={isEditDisabled}
                        style={{ color: T.textPrimary }}
                    />
                ))}
            </div>

            {/* Comments */}
            <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add comments or details..."
                value={resp.comments || ""}
                onChange={handleTextareaChange}
                disabled={isEditDisabled}
                style={{
                    background: T.surface,
                    color: T.textPrimary,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radius,
                    fontFamily,
                    fontSize: fontSize.md,
                    padding: "10px 12px",
                    marginBottom: spacing.lg,
                    resize: "vertical",
                }}
            />
            {messageQuestionId === qid && resMsg && (
                <p style={bodySmall({ color: T.success, marginBottom: spacing.sm })} aria-live="polite">{resMsg}</p>
            )}
            {messageQuestionId === qid && error && (
                <p style={bodySmall({ color: T.danger, marginBottom: 0 })} aria-live="polite">{error}</p>
            )}

            {answered_by && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: spacing['3xl'] }}>
                    <span style={bodySmall({ color: T.textSecondary })}>
                        <b>Answered By:</b> {answered_by}
                    </span>
                </div>
            )}

            {/* Reviewer info */}
            {(reviewer_email || reviewer_adjustment || reviewer_remark) && (
                <div style={{ marginBottom: spacing['3xl'] }}>
                    <h6 style={{ ...body({ color: T.textPrimary }), fontWeight: fontWeight.semibold, marginBottom: spacing.lg }}>Review Info</h6>
                    <div
                        style={{
                            background: T.surfaceMuted,
                            border: `1px solid ${T.border}`,
                            borderRadius: T.radius,
                            padding: spacing['2xl'],
                            ...bodySmall({ color: T.textSecondary }),
                        }}
                    >
                        <p style={{ margin: `0 0 ${spacing.sm}px` }}>Reviewer Email: <b style={{ color: T.textPrimary }}>{reviewer_email || "N/A"}</b></p>
                        <p style={{ margin: `0 0 ${spacing.sm}px` }}>Reviewer Adjustment: <b style={{ color: T.textPrimary }}>{reviewer_adjustment || "N/A"}</b></p>
                        <p style={{ margin: 0 }}>Reviewer Remark: <b style={{ color: T.textPrimary }}>{reviewer_remark || "N/A"}</b></p>
                    </div>
                </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing['2xl'], flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: spacing.lg }}>
                    <span style={{ ...body({ color: T.textPrimary }), fontWeight: fontWeight.semibold }}>Attach Policy:</span>
                    <CommonButton
                        text="Upload"
                        variant="white"
                        disable={isEditDisabled}
                        onClick={() => document.getElementById(`file-${qid}`)?.click()}
                    />
                </div>

                <div style={{ display: "flex", gap: spacing.lg, alignItems: "center" }}>
                    <CommonButton
                        text={checkingId === qid ? "Checking..." : "Check with Foresee"}
                        variant="white"
                        disable={checkingId === qid || isEditDisabled}
                        onClick={handleCheckNova}
                    />
                    <CommonButton
                        text={buttonLabel}
                        variant="success"
                        onClick={handleSubmit}
                        disable={disableSave}
                    />
                </div>

                <Form.Control
                    id={`file-${qid}`}
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    disabled={isEditDisabled}
                />
            </div>

            {/* Uploaded files */}
            {resp.files?.length > 0 && (
                <div style={{ marginTop: spacing['2xl'], display: "flex", gap: spacing.lg, flexWrap: "wrap" }}>
                    {resp.files.map((file, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: "flex",
                                gap: spacing.md,
                                alignItems: "center",
                                background: T.surfaceMuted,
                                border: `1px solid ${T.border}`,
                                borderRadius: T.radiusSm,
                                padding: `${spacing.sm}px ${spacing.lg}px`,
                            }}
                        >
                            <span style={labelFont({ color: T.textSecondary })}>{file?.name}</span>
                            <button
                                type="button"
                                onClick={() => handleRemoveFile(idx)}
                                disabled={isEditDisabled}
                                aria-label="Remove file"
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    color: T.danger,
                                    cursor: isEditDisabled ? "not-allowed" : "pointer",
                                    fontSize: fontSize.lg,
                                    lineHeight: 1,
                                    padding: 0,
                                }}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <ShareControlModal
                shared_with_emails={shared_with_emails || []}
                show={shareModal}
                onHide={closeShareModal}
                controlQuestionId={questionId}
                onRefreshQuestions={onRefreshQuestions}
            />
        </div>
    );
}

// FIX 13: Custom comparison function for React.memo
function areEqual(prevProps, nextProps) {
    // Only re-render if these specific props change
    return (
        prevProps.question?.control_question_id === nextProps.question?.control_question_id &&
        prevProps.response === nextProps.response &&
        prevProps.checkingId === nextProps.checkingId &&
        prevProps.index === nextProps.index &&
        prevProps.resMsg === nextProps.resMsg &&
        prevProps.error === nextProps.error &&
        prevProps.messageQuestionId === nextProps.messageQuestionId &&
        prevProps.loading === nextProps.loading &&
        prevProps.domain === nextProps.domain &&
        prevProps.controlTitle === nextProps.controlTitle &&
        prevProps.submissionStatus === nextProps.submissionStatus
    );
}

export default memo(QuestionCard, areEqual);
